import React, { Fragment, useCallback, useMemo, useRef } from 'react';
import { Animated, FlatListProps, View } from 'react-native';
import lodashIsEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';

import constants from '../Config/constants';
import themeStyle from '../Theme/styles';

import AnimatedHeader, {
  AnimatedHeaderTitle,
  HeaderWithTitleAnimationProps,
} from './HeaderWithTitleAnimation';
import List from './List';
import ListInfiniteScroll from './ListInfiniteScroll';
import ScreenWrapper, { type ScreenWrapperProps } from './ScreenWrapper';

type ScreenWrapperWithTitleAnimationProps = ScreenWrapperProps &
  Partial<FlatListProps<unknown>> &
  PropTypes.InferProps<typeof ListInfiniteScroll.propTypes> & {
    accessoryRight?: React.ReactNode;
    headerProps?: HeaderWithTitleAnimationProps;
    title: string;
    onBackPress?: () => void;
    onRef?: (r: any) => void;
    renderFooter?: () => React.ReactNode;
  };

const ListWrapper = constants.isWeb ? Fragment : ScreenWrapper;

const ScreenWrapperWithTitleAnimation = ({
  accessoryRight,
  children,
  headerProps,
  ListHeaderComponent,
  title,
  onBackPress,
  onRef,
  renderFooter,
  ...rest
}: ScreenWrapperWithTitleAnimationProps) => {
  const scrollRef = useRef(new Animated.Value(0));

  const isInfiniteScroll = typeof rest?.onRequest === 'function';
  const isListPage = !lodashIsEmpty(rest?.data);

  const _renderAnimatedHeaderTitle = useCallback(() => {
    if (constants.isWeb) return null;
    return <AnimatedHeaderTitle>{title}</AnimatedHeaderTitle>;
  }, [title]);

  const getListHeader = useCallback(() => {
    const margin = 26;
    const marginTop = scrollRef.current.interpolate({
      inputRange: [0, 1],
      outputRange: [0, -(margin + 4)],
      extrapolate: 'clamp',
    });
    const marginLeft = scrollRef.current.interpolate({
      inputRange: [0, 1],
      outputRange: [0, margin],
      extrapolate: 'clamp',
    });
    return (
      <View style={themeStyle.pageBackground}>
        <Animated.View style={{ marginTop, marginLeft }}>
          {_renderAnimatedHeaderTitle()}
        </Animated.View>
        {typeof ListHeaderComponent === 'function' ? (
          <ListHeaderComponent />
        ) : (
          ListHeaderComponent ?? null
        )}
      </View>
    );
  }, [ListHeaderComponent, _renderAnimatedHeaderTitle]);

  const scrollProps = useMemo(
    () => ({
      scrollEventThrottle: constants.scrollThrottle,
      onScroll: Animated.event(
        [
          {
            nativeEvent: {
              contentOffset: {
                y: scrollRef.current,
              },
            },
          },
        ],
        { useNativeDriver: false }, // true is not supported onScroll https://stackoverflow.com/a/57402978
      ),
    }),
    [],
  );

  const screenWrapperCommonProps = useMemo(
    () => ({
      bounces: false,
      footer: renderFooter?.(),
      header: constants.isWeb ? null : (
        <AnimatedHeader
          title={title}
          scroll={scrollRef.current}
          accessoryRight={accessoryRight}
          onBackPress={onBackPress}
          withBorder
          {...headerProps}
        />
      ),
    }),
    [accessoryRight, headerProps, title, onBackPress, renderFooter],
  );

  const listCommonProps = useMemo(() => {
    if (!isInfiniteScroll && !isListPage) return null;
    return {
      ref: (r: any) => onRef?.(r),
      ListHeaderComponent: constants.isWeb
        ? ListHeaderComponent
        : getListHeader,
    };
  }, [isInfiniteScroll, isListPage, ListHeaderComponent, getListHeader, onRef]);

  const listMobileProps = useMemo(() => {
    if (constants.isWeb) return null;
    return {
      ...screenWrapperCommonProps,
      scroll: false,
    };
  }, [screenWrapperCommonProps]);

  if (isInfiniteScroll || isListPage) {
    // Infinite Scroll or List with Data Page
    const ListComponent = isInfiniteScroll ? ListInfiniteScroll : List;
    return (
      <ListWrapper {...listMobileProps}>
        <ListComponent {...scrollProps} {...listCommonProps} {...rest} />
      </ListWrapper>
    );
  }

  // Normal Page
  return (
    <ScreenWrapper {...scrollProps} {...screenWrapperCommonProps} {...rest}>
      {_renderAnimatedHeaderTitle()}
      <View style={[themeStyle.flex1, themeStyle.spacingTopMedium]}>
        {children}
      </View>
    </ScreenWrapper>
  );
};

export default ScreenWrapperWithTitleAnimation;
